import { Reducer, RefObject } from 'react';
import { DayNameEnum } from '../../../constants/dates';
import {
  CalendarDataType,
  CalendarHeaderNames,
  FirstTimeslotDataType,
  FormattedDateTime,
  PostingPlanCellDataType,
  TimeslotFilterAction,
  TimeslotFilterType
} from '../../../models/calendar';
import { TimeFormatSetting, TimeslotDay, StartOfWeekFormatSetting } from '../../../models/graphql';
import { TimeslotMappedData } from './PostingPlan.view';
import { format, setDay } from 'date-fns';
import { DateStyle, FormatString } from 'hooks/useDateString';
import { GetTimeslots_workspace_timeslots } from '../../../models/legacy-graphql';
import classNames from 'classnames';

type ClassOverrides = Record<string, string | string[] | undefined>;

const daysOrderStartingMonday = [
  TimeslotDay.MONDAY,
  TimeslotDay.TUESDAY,
  TimeslotDay.WEDNESDAY,
  TimeslotDay.THURSDAY,
  TimeslotDay.FRIDAY,
  TimeslotDay.SATURDAY,
  TimeslotDay.SUNDAY
];

const daysOrderStartingSunday = [
  TimeslotDay.SUNDAY,
  TimeslotDay.MONDAY,
  TimeslotDay.TUESDAY,
  TimeslotDay.WEDNESDAY,
  TimeslotDay.THURSDAY,
  TimeslotDay.FRIDAY,
  TimeslotDay.SATURDAY
];

const filterByDay = (
  data: CalendarDataType<PostingPlanCellDataType>,
  day: TimeslotDay,
  startOfWeek: StartOfWeekFormatSetting
): TimeslotMappedData[] => {
  const dayIndex =
    startOfWeek === StartOfWeekFormatSetting.SUNDAY
      ? daysOrderStartingSunday.findIndex(item => item === day) + 1
      : daysOrderStartingMonday.findIndex(item => item === day) + 1; // we add one since 1st col is axis ( label col with no values)
  let filteredEvents: TimeslotMappedData[] = [];
  data.forEach(row => {
    row.forEach((cell, index) => {
      if (cell && cell.length && dayIndex === index) {
        filteredEvents = filteredEvents.concat(cell);
      }
    });
  });
  return sortByTime(filteredEvents);
};

const getDayFromColumn = (index: number, startOfWeek?: StartOfWeekFormatSetting): TimeslotDay =>
  startOfWeek === StartOfWeekFormatSetting.SUNDAY
    ? daysOrderStartingSunday[index - 1]
    : daysOrderStartingMonday[index - 1];

// @TODO: Consider return just a single object with first & last
const getTimeslotCoordinates = (
  timeslots: CalendarDataType<PostingPlanCellDataType>,
  position: 'FIRST' | 'LAST'
): FirstTimeslotDataType => {
  const isSearchingForFirst = position === 'FIRST';

  // Set default values based on position
  let result: FirstTimeslotDataType = {
    timeslot: false,
    rowIndex: isSearchingForFirst ? 0 : -1,
    colIndex: isSearchingForFirst ? 0 : -1
  };

  for (let i = 0; i < timeslots.length; i++) {
    const rowIndex = isSearchingForFirst ? i : timeslots.length - 1 - i;
    const row = timeslots[rowIndex];

    // ensure row is defined
    if (!row) continue;

    const colIndex = row.findIndex(col => col && col.length > 0);
    const hasTimeslot = colIndex !== -1;

    if (hasTimeslot) {
      result = {
        timeslot: true,
        rowIndex,
        colIndex
      };

      // early return for 'FIRST'
      if (isSearchingForFirst) return result;
    }
  }

  return result;
};

const getHeaderNames = (startWeek: StartOfWeekFormatSetting): CalendarHeaderNames => {
  if (startWeek === StartOfWeekFormatSetting.MONDAY) {
    return ['', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];
  }
  return ['', 'Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
};

const getSortedDayNames = (startOfWeekFormat: StartOfWeekFormatSetting): TimeslotDay[] => {
  const days =
    startOfWeekFormat === StartOfWeekFormatSetting.SUNDAY ? daysOrderStartingSunday : daysOrderStartingMonday;
  return Object.keys(TimeslotDay)
    .map(key => TimeslotDay[key as keyof typeof TimeslotDay])
    .sort((a, b) => days.indexOf(a) - days.indexOf(b));
};
const getTime = (day: TimeslotDay, time: string, timeFormatString: string): FormattedDateTime => {
  const timeFormat =
    timeFormatString === TimeFormatSetting.HOUR_12
      ? FormatString[DateStyle.TIME_ONLY].HOUR_12
      : FormatString[DateStyle.TIME_ONLY].HOUR_24;
  const dayWeekCapitalized = `${day.charAt(0).toUpperCase()}${day.slice(1).toLowerCase()}` as unknown as DayNameEnum;
  const timeArr = time.split(':');
  const hour = parseInt(timeArr[0]);
  const minutes = parseInt(timeArr[1]);
  const dayWeek = parseInt(DayNameEnum[dayWeekCapitalized]);
  const date = setDay(new Date().setHours(hour, minutes), dayWeek);
  const formattedTime = format(date, timeFormat);
  return {
    date: date,
    time: formattedTime
  };
};

const initialCalendarState = (): CalendarDataType<PostingPlanCellDataType> => {
  const hours = 23; // total number of hours, from 00 to 23 - rows
  const calendarData: CalendarDataType<PostingPlanCellDataType> = [
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    []
  ];
  for (let i = 0; i <= hours; i++) {
    calendarData[i] = [undefined, [], [], [], [], [], [], []];
  }
  return calendarData;
};

const calculateOffset = (
  element: HTMLElement,
  parent: HTMLElement,
  direction: 'top' | 'left',
  offset?: number
): number => {
  const elementPosition = element.getBoundingClientRect();
  const parentPosition = parent.getBoundingClientRect();
  const additionalOffset = offset ?? 0;

  return elementPosition[direction] - parentPosition[direction] - additionalOffset;
};
const scrollToTimeslot = (tableRef: RefObject<HTMLTableElement>, rowIndex: number, colIndex: number): void => {
  if (!tableRef.current) return;

  const table = tableRef.current;
  const parentHolder = table.parentElement as HTMLDivElement;

  if (!parentHolder) {
    return;
  }

  const row = table.rows[rowIndex];
  const col = row.cells[colIndex];
  if (!row || !col) return;

  // If we have a sticky/fixed thead, we need to adjust for that
  let theadOffset = 0;
  const thead = table.tHead;

  if (thead) {
    const theadStyles = window.getComputedStyle(thead);
    const isStickyOrFixed = ['sticky', 'fixed'].includes(theadStyles.position);
    if (isStickyOrFixed) {
      theadOffset = thead.getBoundingClientRect().height;
    }
  }

  // Calculate offsets
  const offsetTop = calculateOffset(row, parentHolder, 'top', theadOffset);
  const offsetLeft = calculateOffset(col, parentHolder, 'left');

  // Scroll to both vertical and horizontal positions
  parentHolder.scrollTo({
    top: offsetTop,
    left: offsetLeft,
    behavior: 'smooth'
  });
};

const sortByTime = (events: TimeslotMappedData[]): TimeslotMappedData[] =>
  events.sort((a, b) => {
    if (a.time < b.time) {
      return -1;
    }
    if (a.time > b.time) {
      return 1;
    }
    return 0;
  });

const timeslotFilterReducer: Reducer<TimeslotFilterType, TimeslotFilterAction> = (state, action) => {
  // TempProfiles and tempCategories are used in mobile filter view, since they are not applied before we click apply filters (action applyTempFilters). That action removes temp and sets as categories and profiles
  switch (action.type) {
    case 'showOutOfSeason':
      return { ...state, showOutOfSeason: action.payload };
    case 'categories':
      return { ...state, categories: action.payload };
    case 'profiles':
      return { ...state, profiles: action.payload };
    case 'applyTempFilters':
      return action.payload;
    case 'mobileCategories':
      return { ...state, tempCategories: action.payload, categories: String(action.payload) };
    case 'mobileProfiles':
      return { ...state, tempProfiles: action.payload, profiles: String(action.payload) };
    case 'resetAll':
      return {
        profiles: '',
        categories: '',
        tempProfiles: '',
        tempCategories: '',
        showOutOfSeason: false
      };
    case 'resetCategories':
      return { ...state, categories: '' };
    case 'resetProfiles':
      return { ...state, profiles: '' };
    case 'resetMobileCategories':
      return { ...state, tempCategories: null, categories: '' };
    case 'resetMobileProfiles':
      return { ...state, tempProfiles: null, profiles: '' };
    default:
      return state;
  }
};

const transformEventsData = (
  events: TimeslotMappedData[],
  startDayOfWeek: StartOfWeekFormatSetting
): CalendarDataType<PostingPlanCellDataType> => {
  const data = initialCalendarState();
  const days: Array<TimeslotDay | null> =
    startDayOfWeek === StartOfWeekFormatSetting.MONDAY
      ? [
          null,
          TimeslotDay.MONDAY,
          TimeslotDay.TUESDAY,
          TimeslotDay.WEDNESDAY,
          TimeslotDay.THURSDAY,
          TimeslotDay.FRIDAY,
          TimeslotDay.SATURDAY,
          TimeslotDay.SUNDAY
        ]
      : [
          null,
          TimeslotDay.SUNDAY,
          TimeslotDay.MONDAY,
          TimeslotDay.TUESDAY,
          TimeslotDay.WEDNESDAY,
          TimeslotDay.THURSDAY,
          TimeslotDay.FRIDAY,
          TimeslotDay.SATURDAY
        ];

  days.forEach((day, index) => {
    if (day) {
      const timeslots = sortByTime(events.filter(event => event.day === day));
      timeslots.forEach(timeslot => {
        const hour = +timeslot.time.split(':')[0];
        (data[hour][index] as TimeslotMappedData[]).push(timeslot);
      });
    }
  });

  return data;
};

// Helper function -> move to helpers
const flattenTimeslotsCellData = (timeslots: CalendarDataType<PostingPlanCellDataType>): TimeslotMappedData[] => {
  return timeslots
    .flatMap(row => {
      // Check if row contains the PostingPlanCellDataType array directly
      if (Array.isArray(row)) {
        return row.flatMap(cell => (cell ? cell : []));
      }
      return [];
    })
    .flat();
};

const isDuplicateTimeslot = (
  newTimeslot: GetTimeslots_workspace_timeslots,
  timeslots: CalendarDataType<PostingPlanCellDataType>
): boolean => {
  const newProfileIds = new Set(newTimeslot.socialProfiles.map(profile => profile.id));
  const flattenedTimeslots = flattenTimeslotsCellData(timeslots);

  for (const existingTimeslot of flattenedTimeslots) {
    if (
      existingTimeslot.time === newTimeslot.time &&
      existingTimeslot.day === newTimeslot.day &&
      existingTimeslot.categoryId === newTimeslot.category.id
    ) {
      const existingProfileIds = new Set(existingTimeslot.socialProfiles.map(profile => profile.id));

      if (newProfileIds.size !== existingProfileIds.size) continue;

      if (Array.from(newProfileIds).every(id => existingProfileIds.has(id))) {
        // duplicate found
        return true;
      }
    }
  }

  // no duplicates found
  return false;
};

const findMergeableTimeslot = (
  newTimeslot: GetTimeslots_workspace_timeslots,
  timeslots: CalendarDataType<PostingPlanCellDataType>
): TimeslotMappedData | null => {
  const newProfileIds = new Set(newTimeslot.socialProfiles.map(profile => profile.id));
  const flattenedTimeslots = flattenTimeslotsCellData(timeslots);

  for (const existingTimeslot of flattenedTimeslots) {
    if (
      existingTimeslot.time === newTimeslot.time &&
      existingTimeslot.day === newTimeslot.day &&
      existingTimeslot.categoryId === newTimeslot.category.id
    ) {
      const existingProfileIds = new Set(existingTimeslot.socialProfiles.map(profile => profile.id));

      const needsMerge =
        Array.from(newProfileIds).some(id => !existingProfileIds.has(id)) ||
        Array.from(existingProfileIds).some(id => !newProfileIds.has(id));

      if (needsMerge) {
        // Return timeslot needing merge
        return existingTimeslot;
      }
    }
  }

  // No mergeable timeslot found
  return null;
};

const mergedClasses = (baseClasses: Record<string, string>, overrides?: ClassOverrides): Record<string, string> => {
  const finalClasses: Record<string, string> = {};

  for (const key in baseClasses) {
    const override = overrides?.[key];
    const overrideClasses = override ? (Array.isArray(override) ? override : [override]) : [];

    finalClasses[key] = classNames(baseClasses[key], ...overrideClasses);
  }

  return finalClasses;
};

export const PostingPlanHelpers = {
  filterByDay,
  getDayFromColumn,
  getTimeslotCoordinates,
  getHeaderNames,
  getSortedDayNames,
  getTime,
  initialCalendarState,
  scrollToTimeslot,
  sortByTime,
  timeslotFilterReducer,
  transformEventsData,
  isDuplicateTimeslot,
  findMergeableTimeslot,
  flattenTimeslotsCellData,
  mergedClasses
};
